import { KendraConst, CustomListContent, CustomListObject } from "@amzn/ask-legal-domain";
import { ExpandableSection, SpaceBetween, Box, Badge, Button, Icon } from "@amzn/awsui-components-react";
import { QueryResultItem }  from "@aws-sdk/client-kendra";
import * as React from "react";
import { CustomListContentView } from "../../container/view-content/CustomListContentView";
import { SmartRichTextView } from "../../common/RichTextEditor/SmartRichTextView";
import { SearchInstanceBadge } from "./SearchInstanceBadge";
import { getInstanceIdFromResult, getSearchResultDocumentUrl } from "../../../utils/search-utils";
import { useCurrentInstanceData } from "../../../contexts/InstanceDataContext";
import { CrossInstanceLink } from "./CrossInstanceLink";
import "../../../styles/component/layout/SearchResultPageLayout";

interface FAQAnswersProps {
    instanceId: string;
    result: QueryResultItem[];
}

export const FAQAnswers: React.FC<FAQAnswersProps> = ({ instanceId, result }) => {
    const { instance } = useCurrentInstanceData();
    const sharedPageLibrariesEnabled = instance?.preferences?.features?.sharedPageLibrariesEnabled ?? true;
    const DEFAULT_VIEW_SIZE = 3;
    const [viewableResults, setViewableResults] = React.useState<number>(DEFAULT_VIEW_SIZE);
    const [expanded, setExpanded] = React.useState(false);

    React.useEffect(() => {
        if (!expanded) {
            setViewableResults(DEFAULT_VIEW_SIZE);
        } else if (result.length > DEFAULT_VIEW_SIZE && expanded) {
            setViewableResults(result.length);
        }
    }, [result, expanded]);

    return (
        <React.Fragment>
            <div>
                {result.length === 1 && <h5>Related Question</h5>}
                {result.length > 1 && <h5>Related Questions</h5>}
                {result.slice(0, viewableResults).map(r => (
                    <div key={r.Id} className="search-result-item">
                        <ExpandableSection
                            defaultExpanded={false}
                            header={
                                <SpaceBetween direction="horizontal" size="xs">
                                    <SmartRichTextView
                                        content={r.AdditionalAttributes[0].Value.TextWithHighlightsValue.Text}
                                    />
                                    <CrossInstanceLink
                                        resultItem={r}
                                        currentInstanceId={instanceId}
                                        targetUrl={getSearchResultDocumentUrl({ resultItem: r, instanceId, sharedPageLibrariesEnabled })}
                                        external
                                        externalIconAriaLabel="Opens in a new tab"
                                    >
                                        View
                                    </CrossInstanceLink>
                                    <SpaceBetween direction="horizontal" size="xs">
                                        {r.DocumentAttributes.find(a => a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.LegalOnly)?.Value.StringValue === "true" &&
                                            <Box variant="small" color="text-body-secondary" margin={{right: "xs"}} padding="n">
                                                <Badge color="green">Legal Only</Badge>
                                            </Box>
                                        }
                                        <SearchInstanceBadge
                                            instanceId={getInstanceIdFromResult(r)}
                                        />
                                    </SpaceBetween>
                                </SpaceBetween>
                            }>
                            {r.DocumentAttributes.find(a => a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.ContentType)?.Value.StringValue === "Link" &&
                                <CustomListContentView
                                    content={CustomListContent.create([
                                        CustomListObject.create(r.AdditionalAttributes[0].Value.TextWithHighlightsValue.Text, r.AdditionalAttributes[1].Value.TextWithHighlightsValue.Text)
                                    ])}
                                />}
                            {r.DocumentAttributes.find(a => a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.ContentType)?.Value.StringValue !== "Link" &&
                                <SmartRichTextView
                                    content={r.AdditionalAttributes[1].Value.TextWithHighlightsValue.Text}
                                />}
                        </ExpandableSection>
                    </div>
                ))}
                {result.length > DEFAULT_VIEW_SIZE &&
                    <div className="search-faq-div">
                        <hr className="search-faq-div-hr" />
                        <Button variant="link" onClick={() => { setExpanded(!expanded); }}>
                            {!expanded &&
                                <SpaceBetween size="xs" direction="horizontal">
                                    Show More
                                    <Icon name="caret-down-filled" />
                                </SpaceBetween>}
                            {expanded &&
                                <SpaceBetween size="xs" direction="horizontal">
                                    Show Less
                                    <Icon name="caret-up-filled" />
                                </SpaceBetween>}
                        </Button>
                        <hr className="search-faq-div-hr" />
                    </div>
                }
            </div>
        </React.Fragment>
    );
};
